/**
 * Voice AI — IVR Menu Tab
 * Core menu builder: greeting/prompt config, option list, timeout settings.
 * Dispatches: save, delete, menu-changed, toast
 */

import { LitElement, html } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { ivrStyles } from '../../styles/ivr-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { ivrService } from '../../services/ivr-service.js';
import { confirmService } from '../../services/confirm-service.js';
import { mediaService } from '../../services/media-service.js';
import { mediaLibraryService } from '../../services/media-library-service.js';
import '../voice-modal.js';

const ACTION_ICONS = {
  route_to_agent: 'fa-robot',
  play_message: 'fa-message-lines',
  sub_menu: 'fa-diagram-nested',
  transfer_external: 'fa-phone-arrow-up-right',
  voicemail: 'fa-voicemail',
  repeat_menu: 'fa-rotate-right',
};

const ACTION_LABELS = {
  route_to_agent: 'Route to Agent',
  play_message: 'Play Message',
  sub_menu: 'Sub-Menu',
  transfer_external: 'Transfer External',
  voicemail: 'Voicemail',
  repeat_menu: 'Repeat Menu',
};

export class IvrMenuTab extends LitElement {
  static properties = {
    menu: { type: Object },
    agents: { type: Array },
    saving: { type: Boolean },
    _options: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _showOptionModal: { type: Boolean, state: true },
    _editingOption: { type: Object, state: true },
    _audioLookup: { type: Object, state: true },
    _greetingType: { type: String, state: true },
    _promptType: { type: String, state: true },
    _greetingEnabled: { type: Boolean, state: true },
    _promptEnabled: { type: Boolean, state: true },
    _holdMusicType: { type: String, state: true },
    _holdMusicEnabled: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, ivrStyles];

  constructor() {
    super();
    this.menu = null;
    this.agents = [];
    this.saving = false;
    this._options = [];
    this._loading = true;
    this._showOptionModal = false;
    this._editingOption = null;
    this._lastMenuId = null;
    this._audioLookup = {};
    this._greetingType = 'tts';
    this._promptType = 'tts';
    this._greetingEnabled = true;
    this._promptEnabled = true;
    this._holdMusicType = 'audio';
    this._holdMusicEnabled = true;
    this._previewPlayingId = null;
    this._previewAudioEl = null;
  }

  updated(changed) {
    if (changed.has('menu') && this.menu?.id !== this._lastMenuId) {
      this._lastMenuId = this.menu?.id;
      this._greetingType = this.menu?.greetingType || 'tts';
      this._promptType = this.menu?.promptType || 'tts';
      this._greetingEnabled = (this.menu?.greetingEnabled ?? 1) === 1;
      this._promptEnabled = (this.menu?.promptEnabled ?? 1) === 1;
      this._holdMusicType = this.menu?.holdMusicType || 'audio';
      this._holdMusicEnabled = (this.menu?.holdMusicEnabled ?? 1) === 1;
      this._loadOptions();
      this._loadAudioLookup();
    }
  }

  async _loadOptions() {
    if (!this.menu) return;
    this._loading = true;
    try {
      const data = await ivrService.listOptions(this.menu.id);
      if (data.success) {
        this._options = data.options || [];
      }
    } catch (err) {
      console.error('[IvrMenuTab] Load options failed:', err);
    } finally {
      this._loading = false;
    }
  }

  async _loadAudioLookup() {
    // Load file info for all referenced audio IDs
    const ids = [this.menu?.greetingAudioId, this.menu?.promptAudioId, this.menu?.holdMusicAudioId].filter(Boolean);
    if (ids.length === 0) { this._audioLookup = {}; return; }
    try {
      const data = await mediaService.loadFiles({ fileType: 'audio' });
      if (data.success) {
        const lookup = {};
        for (const f of data.files) { lookup[f.id] = f; }
        this._audioLookup = lookup;
      }
    } catch (err) {
      console.error('[IvrMenuTab] Load audio lookup failed:', err);
    }
  }

  /* ─── Menu Settings Save ─── */

  _save() {
    const menu = { ...this.menu };
    const root = this.shadowRoot;
    menu.name = root.querySelector('#menu-name')?.value || menu.name;
    menu.description = root.querySelector('#menu-description')?.value || '';
    menu.status = root.querySelector('#menu-status')?.value || menu.status;
    menu.greetingType = this._greetingType;
    menu.greetingTts = root.querySelector('#greeting-tts')?.value || '';
    menu.greetingAudioId = this.menu.greetingAudioId || null;
    menu.greetingEnabled = this._greetingEnabled ? 1 : 0;
    menu.promptType = this._promptType;
    menu.promptTts = root.querySelector('#prompt-tts')?.value || '';
    menu.promptAudioId = this.menu.promptAudioId || null;
    menu.promptEnabled = this._promptEnabled ? 1 : 0;
    menu.holdMusicType = this._holdMusicType;
    menu.holdMusicTts = root.querySelector('#hold-music-tts')?.value || '';
    menu.holdMusicAudioId = this.menu.holdMusicAudioId || null;
    menu.holdMusicEnabled = this._holdMusicEnabled ? 1 : 0;
    menu.invalidInputTts = root.querySelector('#invalid-input-tts')?.value || '';
    menu.timeoutSeconds = parseInt(root.querySelector('#timeout-seconds')?.value) || 5;
    menu.maxRetries = parseInt(root.querySelector('#max-retries')?.value) || 3;
    menu.timeoutAction = root.querySelector('#timeout-action')?.value || 'repeat';
    menu.timeoutAgentId = root.querySelector('#timeout-agent')?.value || null;
    if (menu.timeoutAgentId) menu.timeoutAgentId = parseInt(menu.timeoutAgentId);
    menu.timeoutTransferNumber = root.querySelector('#timeout-transfer')?.value || '';
    menu.voiceName = root.querySelector('#voice-name')?.value || 'Polly.Joanna';
    menu.enableExtensionDialing = root.querySelector('#ext-dialing')?.checked ? 1 : 0;

    this.dispatchEvent(new CustomEvent('save', { detail: { menu } }));
  }

  _delete() {
    this.dispatchEvent(new CustomEvent('delete'));
  }

  /* ─── Audio Picker via Media Library ─── */

  async _pickAudio(field) {
    const currentId = this.menu?.[field] || null;
    const result = await mediaLibraryService.show({
      fileType: 'audio',
      tags: 'ivr',
      uploadedBy: 'ivr',
      multiple: false,
      selectedIds: currentId ? [currentId] : [],
      accept: 'audio/*',
    });
    if (!result) return;
    const file = result.files[0];
    if (!file) return;

    // Update the menu property
    this.menu = { ...this.menu, [field]: file.id };
    // Update lookup
    this._audioLookup = { ...this._audioLookup, [file.id]: file };
    this.dispatchEvent(new CustomEvent('menu-changed', { detail: { menu: this.menu } }));
  }

  _clearAudio(field) {
    this.menu = { ...this.menu, [field]: null };
    this.dispatchEvent(new CustomEvent('menu-changed', { detail: { menu: this.menu } }));
  }

  _renderAudioPicker(field) {
    const audioId = this.menu?.[field];
    const file = audioId ? this._audioLookup[audioId] : null;

    if (file) {
      return html`
        <div style="display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-xs);">
          <button class="btn btn-secondary btn-sm" style="min-height:28px;"
            @click=${() => this._pickAudio(field)}>Change</button>
          <button class="btn btn-danger-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
            @click=${() => this._clearAudio(field)} title="Remove">
            <i class="fa-sharp fa-light fa-xmark" style="font-size:0.65rem;"></i>
          </button>
          <button class="btn btn-ghost btn-sm" style="min-height:28px;padding:2px 6px;"
            @click=${() => this._togglePlayPreview(file)} title="Play">
            <i class="fa-sharp fa-light ${this._previewPlayingId === file.id ? 'fa-stop' : 'fa-play'}" style="font-size:0.65rem;"></i>
          </button>
          <span style="flex:1;color:var(--color-text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${file.displayName || file.originalName}</span>
        </div>
      `;
    }

    return html`
      <button class="btn btn-secondary btn-sm"
        @click=${() => this._pickAudio(field)}>
        <i class="fa-sharp fa-light fa-folder-open" style="margin-right:var(--space-1);"></i> Choose from Library
      </button>
    `;
  }

  _togglePlayPreview(file) {
    if (this._previewPlayingId === file.id) {
      this._previewAudioEl?.pause();
      this._previewPlayingId = null;
      this.requestUpdate();
      return;
    }
    if (this._previewAudioEl) this._previewAudioEl.pause();
    this._previewAudioEl = new Audio(file.url);
    this._previewAudioEl.onended = () => { this._previewPlayingId = null; this.requestUpdate(); };
    this._previewAudioEl.play().catch(() => {});
    this._previewPlayingId = file.id;
    this.requestUpdate();
  }

  _formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /* ─── Option Modal ─── */

  _openAddOption() {
    this._editingOption = {
      menu_id: this.menu.id,
      digit: '',
      label: '',
      action_type: 'route_to_agent',
      agent_id: null,
      sub_menu_id: null,
      transfer_number: '',
      message_tts: '',
      voicemail_greeting_tts: '',
      voicemail_max_length: 120,
    };
    this._showOptionModal = true;
  }

  _openEditOption(opt) {
    this._editingOption = { ...opt };
    this._showOptionModal = true;
  }

  _closeOptionModal() {
    this._showOptionModal = false;
    this._editingOption = null;
  }

  async _saveOption() {
    const opt = this._editingOption;
    const root = this.shadowRoot;
    opt.digit = root.querySelector('#opt-digit')?.value || '';
    opt.label = root.querySelector('#opt-label')?.value || '';
    opt.action_type = root.querySelector('#opt-action')?.value || 'route_to_agent';
    opt.agent_id = root.querySelector('#opt-agent')?.value || null;
    if (opt.agent_id) opt.agent_id = parseInt(opt.agent_id);
    opt.transfer_number = root.querySelector('#opt-transfer')?.value || '';
    opt.message_tts = root.querySelector('#opt-message')?.value || '';
    opt.voicemail_greeting_tts = root.querySelector('#opt-vm-greeting')?.value || '';
    opt.voicemail_max_length = parseInt(root.querySelector('#opt-vm-length')?.value) || 120;

    try {
      let data;
      if (opt.id) {
        data = await ivrService.updateOption(opt.id, opt);
      } else {
        data = await ivrService.createOption(opt);
      }
      if (data.success) {
        this._closeOptionModal();
        this._loadOptions();
        this._toast(opt.id ? 'Option updated' : 'Option added');
      } else {
        this._toast('Save failed: ' + (data.error || 'Unknown'));
      }
    } catch (err) {
      this._toast('Save failed: ' + err.message);
    }
  }

  async _confirmDeleteOption(id) {
    const confirmed = await confirmService.show({
      title: 'Delete Option',
      icon: 'fa-triangle-exclamation',
      description: 'This action cannot be undone.',
    });
    if (!confirmed) return;
    try {
      const data = await ivrService.deleteOption(id);
      if (data.success) {
        this._loadOptions();
        this._toast('Option deleted');
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message);
    }
  }

  _toast(message) {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message } }));
  }

  /* ─── Render ─── */

  render() {
    if (!this.menu) return html``;
    const m = this.menu;

    return html`
      ${iconStyles}

      <!-- Menu Settings -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-gear"></i> Menu Settings</div>
            <div class="section-description">Configure the menu name, status, and voice settings</div>
          </div>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Menu Name</label>
            <input type="text" class="form-input" id="menu-name" .value=${m.name}>
          </div>
          <div class="form-group">
            <label class="form-label">Status</label>
            <select class="form-select" id="menu-status">
              <option value="draft" ?selected=${m.status === 'draft'}>Draft</option>
              <option value="active" ?selected=${m.status === 'active'}>Active</option>
              <option value="inactive" ?selected=${m.status === 'inactive'}>Inactive</option>
            </select>
          </div>
          <div class="form-group full">
            <label class="form-label">Description</label>
            <input type="text" class="form-input" id="menu-description" .value=${m.description || ''} placeholder="Optional description">
          </div>
          <div class="form-group">
            <label class="form-label">TTS Voice</label>
            <input type="text" class="form-input" id="voice-name" .value=${m.voiceName || 'Polly.Joanna'}>
            <div class="form-hint">Amazon Polly voice name</div>
          </div>
          <div class="form-group">
            <label class="form-label">Extension Dialing</label>
            <div class="toggle-row">
              <div class="toggle ${m.enableExtensionDialing ? 'active' : ''}" id="ext-dialing-toggle"
                @click=${() => {
                  const cb = this.shadowRoot.querySelector('#ext-dialing');
                  cb.checked = !cb.checked;
                  this.shadowRoot.querySelector('#ext-dialing-toggle').classList.toggle('active', cb.checked);
                }}>
              </div>
              <input type="checkbox" id="ext-dialing" ?checked=${m.enableExtensionDialing} hidden>
              <span class="toggle-label">Allow callers to dial extensions</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Greeting -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-microphone"></i> Greeting</div>
            <div class="section-description">Played once when the caller first reaches this menu. Callers can press digits during greeting.</div>
          </div>
          <div class="toggle ${this._greetingEnabled ? 'active' : ''}"
            @click=${() => { this._greetingEnabled = !this._greetingEnabled; }}></div>
        </div>

        <div class="section-content ${this._greetingEnabled ? '' : 'disabled'}">
          <div class="type-switcher">
            <button class="type-switcher-btn ${this._greetingType === 'tts' ? 'active' : ''}"
              @click=${() => { this._greetingType = 'tts'; }}>
              <i class="fa-sharp fa-light fa-text"></i> Text-to-Speech
            </button>
            <button class="type-switcher-btn ${this._greetingType === 'audio' ? 'active' : ''}"
              @click=${() => { this._greetingType = 'audio'; }}>
              <i class="fa-sharp fa-light fa-waveform-lines"></i> Audio File
            </button>
          </div>

          ${this._greetingType === 'audio' ? html`
            <div class="form-group">
              <label class="form-label">Audio File</label>
              ${this._renderAudioPicker('greetingAudioId')}
            </div>
            <textarea id="greeting-tts" hidden>${m.greetingTts || ''}</textarea>
          ` : html`
            <div class="form-group">
              <label class="form-label">Greeting Text</label>
              <textarea class="form-textarea" id="greeting-tts" rows="3"
                placeholder="e.g. Thank you for calling Sunset RV Park.">${m.greetingTts || ''}</textarea>
            </div>
          `}
        </div>
      </div>

      <!-- Prompt -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-comment-dots"></i> Prompt</div>
            <div class="section-description">Tells callers their options (e.g. "Press 1 for Reservations")</div>
          </div>
          <div class="toggle ${this._promptEnabled ? 'active' : ''}"
            @click=${() => { this._promptEnabled = !this._promptEnabled; }}></div>
        </div>

        <div class="section-content ${this._promptEnabled ? '' : 'disabled'}">
          <div class="type-switcher">
            <button class="type-switcher-btn ${this._promptType === 'tts' ? 'active' : ''}"
              @click=${() => { this._promptType = 'tts'; }}>
              <i class="fa-sharp fa-light fa-text"></i> Text-to-Speech
            </button>
            <button class="type-switcher-btn ${this._promptType === 'audio' ? 'active' : ''}"
              @click=${() => { this._promptType = 'audio'; }}>
              <i class="fa-sharp fa-light fa-waveform-lines"></i> Audio File
            </button>
          </div>

          ${this._promptType === 'audio' ? html`
            <div class="form-group">
              <label class="form-label">Audio File</label>
              ${this._renderAudioPicker('promptAudioId')}
            </div>
            <textarea id="prompt-tts" hidden>${m.promptTts || ''}</textarea>
          ` : html`
            <div class="form-group">
              <label class="form-label">Prompt Text</label>
              <textarea class="form-textarea" id="prompt-tts" rows="3"
                placeholder="e.g. Press 1 for Reservations, Press 2 for the Front Desk.">${m.promptTts || ''}</textarea>
            </div>
          `}
        </div>
      </div>

      <!-- Hold Music -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-music"></i> Hold Music</div>
            <div class="section-description">Played while transferring the call to an agent or external number</div>
          </div>
          <div class="toggle ${this._holdMusicEnabled ? 'active' : ''}"
            @click=${() => { this._holdMusicEnabled = !this._holdMusicEnabled; }}></div>
        </div>

        <div class="section-content ${this._holdMusicEnabled ? '' : 'disabled'}">
          <div class="type-switcher">
            <button class="type-switcher-btn ${this._holdMusicType === 'tts' ? 'active' : ''}"
              @click=${() => { this._holdMusicType = 'tts'; }}>
              <i class="fa-sharp fa-light fa-text"></i> Text-to-Speech
            </button>
            <button class="type-switcher-btn ${this._holdMusicType === 'audio' ? 'active' : ''}"
              @click=${() => { this._holdMusicType = 'audio'; }}>
              <i class="fa-sharp fa-light fa-waveform-lines"></i> Audio File
            </button>
          </div>

          ${this._holdMusicType === 'audio' ? html`
            <div class="form-group">
              <label class="form-label">Audio File</label>
              ${this._renderAudioPicker('holdMusicAudioId')}
            </div>
            <textarea id="hold-music-tts" hidden>${m.holdMusicTts || ''}</textarea>
          ` : html`
            <div class="form-group">
              <label class="form-label">Hold Message</label>
              <textarea class="form-textarea" id="hold-music-tts" rows="3"
                placeholder="e.g. Please hold while we connect you.">${m.holdMusicTts || ''}</textarea>
            </div>
          `}
        </div>
      </div>

      <!-- Menu Options -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-keyboard"></i> Menu Options</div>
            <div class="section-description">Key press actions — what happens when the caller presses a digit</div>
          </div>
          <button class="btn btn-primary btn-sm" @click=${this._openAddOption}>
            <i class="fa-sharp fa-light fa-plus"></i> Add Option
          </button>
        </div>

        ${this._loading ? html`
          ${[1, 2, 3].map(() => html`
            <div class="option-row">
              <div class="skeleton-line" style="width:36px;height:36px;border-radius:var(--radius-md);"></div>
              <div class="option-info">
                <div class="skeleton-line" style="width:60%;height:14px;margin-bottom:6px;"></div>
                <div class="skeleton-line xs" style="width:40%;"></div>
              </div>
            </div>
          `)}
        ` : this._options.length === 0 ? html`
          <div class="empty-state">
            <div class="empty-state-icon"><i class="fa-sharp fa-light fa-keyboard"></i></div>
            <div class="empty-state-title">No options configured</div>
            <div class="empty-state-description">Add key press options to route callers.</div>
          </div>
        ` : html`
          ${this._options.map(opt => html`
            <div class="option-row">
              <div class="option-digit">${opt.digit}</div>
              <div class="option-info">
                <div class="option-label">${opt.label}</div>
                <div class="option-action">
                  <i class="fa-sharp fa-light ${ACTION_ICONS[opt.action_type] || 'fa-circle-question'}"></i>
                  ${ACTION_LABELS[opt.action_type] || opt.action_type}
                  ${opt.agent_name ? html` <span style="color:var(--color-accent);">&mdash; ${opt.agent_name}</span>` : ''}
                  ${opt.sub_menu_name ? html` <span style="color:var(--color-accent);">&mdash; ${opt.sub_menu_name}</span>` : ''}
                  ${opt.transfer_number ? html` <span style="color:var(--color-accent);">&mdash; ${opt.transfer_number}</span>` : ''}
                </div>
              </div>
              <div class="option-actions">
                <button title="Edit" @click=${() => this._openEditOption(opt)}>
                  <i class="fa-sharp fa-light fa-pen-to-square"></i>
                </button>
                <button class="danger" title="Delete" @click=${() => this._confirmDeleteOption(opt.id)}>
                  <i class="fa-sharp fa-light fa-trash"></i>
                </button>
              </div>
            </div>
          `)}
        `}
      </div>

      <!-- Timeout Settings -->
      <div class="section">
        <div class="section-header">
          <div>
            <div class="section-title"><i class="fa-sharp fa-light fa-hourglass-clock"></i> Timeout & Fallback</div>
            <div class="section-description">What happens when the caller doesn't press anything</div>
          </div>
        </div>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Timeout (seconds)</label>
            <input type="number" class="form-input" id="timeout-seconds" min="1" max="30" .value=${String(m.timeoutSeconds || 5)}>
            <div class="form-hint">Wait time before retry</div>
          </div>
          <div class="form-group">
            <label class="form-label">Max Retries</label>
            <input type="number" class="form-input" id="max-retries" min="1" max="10" .value=${String(m.maxRetries || 3)}>
            <div class="form-hint">Times to replay prompt</div>
          </div>
          <div class="form-group full">
            <label class="form-label">Invalid Input Message</label>
            <input type="text" class="form-input" id="invalid-input-tts" .value=${m.invalidInputTts || ''} placeholder="Sorry, that is not a valid option.">
            <div class="form-hint">Played when an unrecognized key is pressed</div>
          </div>
          <div class="form-group">
            <label class="form-label">Timeout Action</label>
            <select class="form-select" id="timeout-action">
              <option value="repeat" ?selected=${m.timeoutAction === 'repeat'}>Repeat Menu</option>
              <option value="route_to_agent" ?selected=${m.timeoutAction === 'route_to_agent'}>Route to Agent</option>
              <option value="voicemail" ?selected=${m.timeoutAction === 'voicemail'}>Voicemail</option>
              <option value="transfer_external" ?selected=${m.timeoutAction === 'transfer_external'}>Transfer External</option>
              <option value="hangup" ?selected=${m.timeoutAction === 'hangup'}>Hang Up</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Timeout Agent</label>
            <select class="form-select" id="timeout-agent">
              <option value="">-- None --</option>
              ${(this.agents || []).map(a => html`
                <option value="${a.id}" ?selected=${m.timeoutAgentId === a.id}>${a.name}</option>
              `)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Transfer Number</label>
            <input type="text" class="form-input" id="timeout-transfer" .value=${m.timeoutTransferNumber || ''} placeholder="+15551234567">
            <div class="form-hint">For external transfer action</div>
          </div>
        </div>
      </div>

      <!-- Actions Footer -->
      <div class="actions-footer">
        <button class="btn btn-danger-ghost" @click=${this._delete}>
          <i class="fa-sharp fa-light fa-trash"></i> Delete Menu
        </button>
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this.saving}>
          <i class="fa-sharp fa-light fa-floppy-disk"></i>
          ${this.saving ? 'Saving...' : 'Save Menu'}
        </button>
      </div>

      <!-- Option Modal -->
      ${this._renderOptionModal()}

    `;
  }

  _renderOptionModal() {
    const opt = this._editingOption;
    const isEdit = !!opt?.id;

    return html`
      <voice-modal ?open=${this._showOptionModal} title="${isEdit ? 'Edit Option' : 'Add Option'}" width="540px"
        @modal-close=${this._closeOptionModal}>
        <div style="padding:var(--space-6, 24px);">
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Digit</label>
            <select class="form-select" id="opt-digit">
              ${['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'].map(d => html`
                <option value="${d}" ?selected=${opt?.digit === d}>${d}</option>
              `)}
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Label</label>
            <input type="text" class="form-input" id="opt-label" .value=${opt?.label || ''} placeholder="e.g. Reservations">
          </div>
          <div class="form-group full">
            <label class="form-label">Action</label>
            <select class="form-select" id="opt-action" @change=${() => this.requestUpdate()}>
              ${Object.entries(ACTION_LABELS).map(([k, v]) => html`
                <option value="${k}" ?selected=${opt?.action_type === k}>${v}</option>
              `)}
            </select>
          </div>
        </div>

        <!-- Action-specific fields -->
        ${this._renderActionFields(opt)}
        </div>

        <div slot="footer">
          <button class="btn btn-secondary" @click=${this._closeOptionModal}>Cancel</button>
          <button class="btn btn-primary" @click=${this._saveOption}>
            ${isEdit ? 'Update' : 'Add'} Option
          </button>
        </div>
      </voice-modal>
    `;
  }

  _renderActionFields(opt) {
    const action = this.shadowRoot?.querySelector('#opt-action')?.value || opt?.action_type || 'route_to_agent';

    switch (action) {
      case 'route_to_agent':
        return html`
          <div class="form-group">
            <label class="form-label">Agent</label>
            <select class="form-select" id="opt-agent">
              <option value="">-- Select Agent --</option>
              ${(this.agents || []).map(a => html`
                <option value="${a.id}" ?selected=${opt?.agent_id === a.id}>${a.name}</option>
              `)}
            </select>
          </div>
        `;
      case 'transfer_external':
        return html`
          <div class="form-group">
            <label class="form-label">Transfer Number</label>
            <input type="text" class="form-input" id="opt-transfer" .value=${opt?.transfer_number || ''} placeholder="+15551234567">
          </div>
        `;
      case 'play_message':
        return html`
          <div class="form-group">
            <label class="form-label">Message Text (TTS)</label>
            <textarea class="form-textarea" id="opt-message" rows="3" placeholder="Message to play...">${opt?.message_tts || ''}</textarea>
          </div>
        `;
      case 'voicemail':
        return html`
          <div class="form-grid">
            <div class="form-group full">
              <label class="form-label">Voicemail Greeting (TTS)</label>
              <textarea class="form-textarea" id="opt-vm-greeting" rows="2">${opt?.voicemail_greeting_tts || ''}</textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Max Length (seconds)</label>
              <input type="number" class="form-input" id="opt-vm-length" min="10" max="600" .value=${String(opt?.voicemail_max_length || 120)}>
            </div>
          </div>
        `;
      default:
        return html`
          <input type="hidden" id="opt-agent" value="">
          <input type="hidden" id="opt-transfer" value="">
          <input type="hidden" id="opt-message" value="">
          <input type="hidden" id="opt-vm-greeting" value="">
          <input type="hidden" id="opt-vm-length" value="120">
        `;
    }
  }
}

customElements.define('ivr-menu-tab', IvrMenuTab);
